/** Pure presentation for the firm-side Client assets tab: message ids, filtering and table rows. */
export type ClientAssetTypeKey = 'deliverable' | 'reference' | 'input_request' | 'client_response';
export type AssetFilter = 'all' | 'deliverable' | 'reference' | 'input_request';

export type ClientAssetLike = {
	id: string;
	assetType: string;
	name: string;
	status: string;
	archived: boolean;
	dueDate: string | null;
	taskName: string | null;
	currentVersion: {versionNo: number} | null;
	versions: ReadonlyArray<{versionNo: number}>;
	responses: ReadonlyArray<{id: string}>;
	readReceipts: ReadonlyArray<{email: string; versionNo: number; readAt: string}>;
};

export const typeMessageId = (t: string): string => `client_assets.type.${t}`;
export const statusMessageId = (a: Pick<ClientAssetLike, 'status' | 'archived'>): string =>
	a.archived ? 'client_assets.status.archived' : `client_assets.status.${a.status}`;

export const filterAssets = <T extends Pick<ClientAssetLike, 'assetType'>>(list: ReadonlyArray<T>, f: AssetFilter): T[] =>
	f === 'all' ? [...list] : list.filter(a => a.assetType === f);

export function formatBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${Math.round((n / 1024) * 10) / 10} KB`;
	return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
}

export type MessageRef = {id: string; values?: Record<string, string | number>};

export function engagementLine(a: ClientAssetLike): MessageRef | null {
	if (a.assetType === 'reference') {
		const r = a.readReceipts[0];
		return r ? {id: 'client_assets.engagement.read_by', values: {email: r.email, date: r.readAt.slice(0, 10)}} : null;
	}
	if (a.assetType === 'input_request') {
		if (a.versions.length > 0) return {id: 'client_assets.engagement.uploads', values: {count: a.versions.length}};
		return a.dueDate ? {id: 'client_assets.engagement.due', values: {date: a.dueDate}} : null;
	}
	return a.responses.length > 0 ? {id: 'client_assets.engagement.responses', values: {count: a.responses.length}} : null;
}

export type ClientAssetRow = {
	id: string;
	name: string;
	type: string;
	version: string;
	task: string;
	status: string;
	engagement: string;
};

/** Precomputed plain rows: the DS Table's TextColumn renders accessor strings only. */
export function toRows(
	list: ReadonlyArray<ClientAssetLike>,
	t: (id: string, values?: Record<string, string | number>) => string
): ClientAssetRow[] {
	return list.map(a => {
		const e = engagementLine(a);
		return {
			id: a.id,
			name: a.name,
			type: t(typeMessageId(a.assetType)),
			version: a.currentVersion ? `v${a.currentVersion.versionNo}` : '—',
			task: a.taskName ?? t('client_assets.project_level'),
			status: t(statusMessageId(a)),
			engagement: e ? t(e.id, e.values) : '—',
		};
	});
}
